"use strict";
/**
 * useStrategyStructure Hook
 *
 * Derives strategy structure from an already-fetched strategies list and manages
 * save operations. Accepts the list + createdAt from the caller (useStrategies)
 * so no additional network request is made.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("react");
var ai_hub_strategy_api_service_1 = __importDefault(require("../services/ai-hub-strategy-api.service"));
/**
 * Manage strategy structure derived from an already-fetched strategies list.
 * No additional API call is made – data is extracted from the provided list.
 */
var useStrategyStructure = function (strategyId, strategies, externalCreatedAt) {
    var _a = react_1.useState([]), data = _a[0], setData = _a[1];
    var _b = react_1.useState(externalCreatedAt), createdAt = _b[0], setCreatedAt = _b[1];
    var _c = react_1.useState(null), error = _c[0], setError = _c[1];
    var _d = react_1.useState(false), isSaving = _d[0], setIsSaving = _d[1];
    var _e = react_1.useState(null), saveError = _e[0], setSaveError = _e[1];
    // Derive structure from the provided strategies list whenever it or the selected ID changes
    react_1.useEffect(function () {
        if (!strategyId) {
            setData([]);
            setError(null);
            return;
        }
        var strategy = strategies.find(function (s) { return s.id === strategyId; });
        if (strategy) {
            setData(strategy.structure || []);
            setError(null);
        }
        else if (strategies.length > 0) {
            setData([]);
            setError("Strategy with ID " + strategyId + " not found");
        }
    }, [strategyId, strategies]);
    // Keep internal createdAt in sync with the external value
    react_1.useEffect(function () {
        setCreatedAt(externalCreatedAt);
    }, [externalCreatedAt]);
    var save = react_1.useCallback(function (structure, strategies, currentSessionChangeHistory) { return __awaiter(void 0, void 0, void 0, function () {
        var strategyIndex, updatedStrategies, normalizedStrategies, config, result, updatedStrategy, updatedStructure, err_1, errorMessage;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!strategyId) {
                        setSaveError('No strategy ID provided');
                        return [2 /*return*/, null];
                    }
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, 4, 5]);
                    setIsSaving(true);
                    setSaveError(null);
                    strategyIndex = strategies.findIndex(function (s) { return s.id === strategyId; });
                    if (strategyIndex === -1) {
                        throw new Error("Strategy with ID " + strategyId + " not found");
                    }
                    updatedStrategies = __spreadArrays(strategies);
                    updatedStrategies[strategyIndex] = __assign(__assign({}, updatedStrategies[strategyIndex]), { structure: structure });
                    normalizedStrategies = updatedStrategies.map(function (strategy) { return ({
                        id: strategy.id,
                        name: strategy.name,
                        structure: strategy.structure || [],
                    }); });
                    config = {
                        strategies: normalizedStrategies,
                        changeHistory: currentSessionChangeHistory || null,
                    };
                    if (createdAt) {
                        config.createdAt = createdAt;
                    }
                    return [4 /*yield*/, ai_hub_strategy_api_service_1.default.saveConfiguration(config)];
                case 2:
                    result = _b.sent();
                    updatedStrategy = result.strategies.find(function (s) { return s.id === strategyId; });
                    updatedStructure = (updatedStrategy === null || updatedStrategy === void 0 ? void 0 : updatedStrategy.structure) || [];
                    setData(updatedStructure);
                    setCreatedAt(result.createdAt || null);
                    return [2 /*return*/, {
                            structure: updatedStructure,
                            allStrategies: result.strategies || [],
                            savedCreatedAt: result.createdAt || null,
                        }];
                case 3:
                    err_1 = _b.sent();
                    errorMessage = err_1 instanceof Error ? err_1.message : 'Failed to save strategy structure';
                    setSaveError(errorMessage);
                    // Re-throw invalid-response errors so the orchestrator can pick
                    // the INVALID_RESPONSE_MESSAGE banner instead of the generic
                    // SAVE_FAILED_MESSAGE. Other (transport-level) errors are
                    // signalled via the existing `null` return contract.
                    if ((_a = err_1) === null || _a === void 0 ? void 0 : _a.isInvalidResponse) {
                        throw err_1;
                    }
                    return [2 /*return*/, null];
                case 4:
                    setIsSaving(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [strategyId, createdAt]);
    return {
        data: data,
        error: error,
        save: save,
        isSaving: isSaving,
        saveError: saveError,
    };
};
exports.default = useStrategyStructure;
